import type { FieldLike } from './types';

export interface GroupNode<Row> {
  key: string;
  label: string;
  fieldName: string;
  fieldSlug: string;
  rawValue: string;
  depth: number;
  items: Row[];
  children: GroupNode<Row>[] | null;
}

export interface GroupingLevelLike {
  fieldSlug: string;
  order: 'name' | 'count';
}

export interface BuildGroupTreeOptions<Row> {
  /** Извлечь raw-значение поля из строки. */
  getRawValue: (row: Row, fieldSlug: string) => unknown;
  /** Получить FieldLike по slug. */
  resolveField: (slug: string) => FieldLike | undefined;
  /** Преобразовать raw-ключ в человекочитаемую метку (опции, label справочника и т.п.). */
  resolveLabel?: (fieldSlug: string, rawKey: string) => string;
}

/**
 * Компаратор ключей групп по имени. Для select / multi_select с опциями
 * порядок берётся из настроек поля (порядок опций), а не из алфавита:
 * значения вне списка — после всех опций, «пусто» — всегда в конце.
 * Для остальных типов — сравнение по алфавиту (ru).
 *
 * `multiSep` — разделитель составного ключа для многозначных полей
 * (запись с [A, B] образует одну группу «A␁B»); позиция такой группы —
 * минимальная позиция среди её значений.
 */
export function makeGroupKeyComparator(
  field: FieldLike | undefined,
  multiSep?: string,
): (a: string, b: string) => number {
  const optionPos = selectOptionPositions(field);
  const keyPos = (key: string): number => {
    if (!optionPos) return 0;
    const parts = multiSep ? key.split(multiSep) : [key];
    let best = Number.MAX_SAFE_INTEGER;
    for (const p of parts) {
      const pos = optionPos.get(p);
      if (pos !== undefined && pos < best) best = pos;
    }
    return best;
  };
  return (a, b) => {
    if (a === '__empty__') return b === '__empty__' ? 0 : 1;
    if (b === '__empty__') return -1;
    if (optionPos) {
      const d = keyPos(a) - keyPos(b);
      if (d !== 0) return d;
    }
    return a.localeCompare(b, 'ru');
  };
}

function selectOptionPositions(field: FieldLike | undefined): Map<string, number> | null {
  if (!field) return null;
  if (field.fieldType !== 'select' && field.fieldType !== 'multi_select') return null;
  const raw = (field.config as { options?: unknown } | null)?.options;
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const map = new Map<string, number>();
  raw.forEach((o, i) => {
    const v = o && typeof o === 'object' ? (o as { value?: unknown }).value : undefined;
    if (typeof v === 'string' && !map.has(v)) map.set(v, i);
  });
  return map.size > 0 ? map : null;
}

/**
 * Строит вложенное дерево групп. Generic — принимает произвольный Row.
 * Поддерживает массивы значений (multi_select / multi-reference) — строка
 * попадает в каждую группу, ключ которой содержится в массиве.
 */
export function buildGroupTree<Row>(
  rows: Row[],
  levels: GroupingLevelLike[],
  options: BuildGroupTreeOptions<Row>,
): GroupNode<Row>[] | null {
  return buildLevel(rows, levels, 0, '', options);
}

function buildLevel<Row>(
  rows: Row[],
  levels: GroupingLevelLike[],
  depth: number,
  parentKey: string,
  options: BuildGroupTreeOptions<Row>,
): GroupNode<Row>[] | null {
  if (levels.length === 0) return null;
  const [level, ...rest] = levels;
  if (!level) return null;

  const field = options.resolveField(level.fieldSlug);
  const fieldName = field?.name ?? level.fieldSlug;

  const buckets = new Map<string, Row[]>();
  const pushTo = (key: string, row: Row) => {
    const arr = buckets.get(key) ?? [];
    arr.push(row);
    buckets.set(key, arr);
  };

  for (const row of rows) {
    const raw = options.getRawValue(row, level.fieldSlug);
    if (Array.isArray(raw)) {
      const arr = raw as unknown[];
      if (arr.length === 0) {
        pushTo('__empty__', row);
      } else {
        for (const item of arr) {
          if (item === null || item === undefined || item === '') pushTo('__empty__', row);
          else if (typeof item === 'boolean') pushTo(item ? 'true' : 'false', row);
          else pushTo(String(item), row);
        }
      }
      continue;
    }
    let key: string;
    if (raw === null || raw === undefined || raw === '') key = '__empty__';
    else if (typeof raw === 'boolean') key = raw ? 'true' : 'false';
    else key = String(raw);
    pushTo(key, row);
  }

  const entries = Array.from(buckets.entries());
  if (level.order === 'count') {
    entries.sort((a, b) => b[1].length - a[1].length);
  } else {
    const cmp = makeGroupKeyComparator(field);
    entries.sort((a, b) => cmp(a[0], b[0]));
  }

  return entries.map(([rawKey, groupItems]) => {
    const compositeKey = parentKey
      ? `${parentKey}|${level.fieldSlug}=${rawKey}`
      : `${level.fieldSlug}=${rawKey}`;
    return {
      key: compositeKey,
      label:
        rawKey === '__empty__'
          ? '— пусто —'
          : (options.resolveLabel?.(level.fieldSlug, rawKey) ?? rawKey),
      fieldName,
      fieldSlug: level.fieldSlug,
      rawValue: rawKey,
      depth,
      items: groupItems,
      children: rest.length > 0 ? buildLevel(groupItems, rest, depth + 1, compositeKey, options) : null,
    };
  });
}
